/**
 * Sticky PDP add-to-cart bar — mobile only.
 * Markup/styles: snippets/component-cart-bar-mobile.liquid
 *
 * Design principle (live parity, snippets/sticky-add-to-cart-c.liquid +
 * assets/sticky-add-to-cart-c.js): this file never reimplements color-swap,
 * availability, or add-to-cart logic. It reads state off the real,
 * already-rendered native controls and, on a pick, proxies it with a real
 * .click() on the matching native element so the theme's existing handlers
 * (section-product.js's color swap, cart.js's add-to-cart) run exactly as
 * they do for a real user click. Re-reads happen off a single
 * MutationObserver on <product-info>, since every native update (color swap,
 * size pick) eventually mutates something under it.
 */

/**
 * @typedef {object} CartBarMobileOption
 * @property {string} value
 * @property {boolean} checked
 * @property {boolean} soldOut
 * @property {string} [swatchStyle] Color options only.
 * @property {Element} proxy Native control clicked on pick.
 */

const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * Escapes a value for safe use in HTML text and attributes.
 * @param {*} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, (ch) => HTML_ENTITIES[ch]);
}

/**
 * Colors are separate sibling products (component-product-siblings.liquid),
 * not a real Shopify option, so there is no "sold out" state to read here —
 * matches live, which never marks a color sold out in this bar either.
 * @param {Element} productInfo
 * @returns {CartBarMobileOption[]}
 */
function readColorOptions(productInfo) {
  const swatches = productInfo.querySelectorAll('.product-siblings__swatch[data-sibling-swap]');

  return Array.from(swatches, (swatch) => {
    const fill = swatch.querySelector('.product-siblings__swatch-fill');
    const img = swatch.querySelector('.product-siblings__swatch-image');
    // Not `.visually-hidden` (a sibling of this <a>, not a descendant, and
    // raw/unfiltered) — the tooltip already carries Liquid's `| capitalize`,
    // which this bar's own CSS text-transform then title-cases per word.
    const tooltip = swatch.closest('.product-siblings__item')?.querySelector('.product-siblings__tooltip');
    let swatchStyle = '';

    if (fill) {
      swatchStyle = fill.getAttribute('style') || '';
    } else if (img) {
      swatchStyle = `background-image:url(${img.src});`;
    }

    return {
      value: (tooltip ? tooltip.textContent : swatch.getAttribute('aria-label') || '').trim(),
      checked: swatch.classList.contains('product-siblings__swatch--active'),
      soldOut: swatch.dataset.available === 'false',
      swatchStyle,
      proxy: swatch,
    };
  });
}

/**
 * The real (non-color) option — usually Size — rendered as pill radios by
 * the "button" picker_type in sections/product.liquid.
 * @param {Element} productInfo
 * @returns {CartBarMobileOption[]}
 */
function readSizeOptions(productInfo) {
  const radios = productInfo.querySelectorAll('[data-variant-buttons] .pill-radio');
  if (!radios.length) return [];

  return Array.from(radios, (input) => ({
    value: input.value,
    checked: input.checked,
    soldOut: input.classList.contains('disabled'),
    proxy: input,
  }));
}

/**
 * Ready to add exactly when the real button is in its "submit" state —
 * sections/component-product-buy-buttons.liquid only gives the button an id
 * once a valid, in-stock variant is resolved (placeholder/notify states
 * render without one). No separate disabled/hidden check needed.
 * @param {string} sectionId
 * @returns {HTMLButtonElement | null}
 */
function getRealAddToCartBtn(sectionId) {
  return document.getElementById(`AddToCart-${sectionId}`);
}

/**
 * Color swatch dot markup shared by the list options and the trigger.
 * @param {string} swatchStyle
 * @returns {string}
 */
function colorDotHtml(swatchStyle) {
  return `<span class="cart-bar-mobile__color-dot" style="${swatchStyle}"></span>`;
}

class CartBarMobileSelect extends HTMLElement {
  constructor() {
    super();
    // Bound once so a re-parent (disconnect + connect) re-adds the same references.
    this._onTriggerClick = this.toggle.bind(this);
    this._onListClick = this.onListClick.bind(this);
    this._onDocumentClick = this.onDocumentClick.bind(this);
    this._onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.soldOutText = this.closest('cart-bar-mobile')?.dataset.soldOutText || '';
    this.trigger = this.querySelector('[data-select-trigger]');
    this.triggerContent = this.querySelector('[data-select-trigger-content]');
    this.list = this.querySelector('[data-select-list]');
    this.selectType = this.dataset.selectType;
    this.options = [];
    this.selectedValue = null;

    this.trigger.addEventListener('click', this._onTriggerClick);
    this.list.addEventListener('click', this._onListClick);
    document.addEventListener('click', this._onDocumentClick);
    this.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this._onTriggerClick);
    this.list?.removeEventListener('click', this._onListClick);
    document.removeEventListener('click', this._onDocumentClick);
    this.removeEventListener('keydown', this._onKeyDown);
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.classList.contains('is-open');
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.dispatchEvent(new CustomEvent('cart-bar-mobile-select:opening', { bubbles: true }));
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * Closes the list on any click outside this select.
   * @param {MouseEvent} e
   */
  onDocumentClick(e) {
    if (!this.contains(e.target)) this.close();
  }

  /**
   * Escape closes; arrow keys (and Home/End while open) move focus between enabled options.
   * @param {KeyboardEvent} e
   */
  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.trigger.focus();
      return;
    }

    const optionEls = this.getEnabledOptionEls();
    const currentIndex = optionEls.indexOf(document.activeElement);
    let target = null;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!this.isOpen) this.open();

      if (currentIndex === -1) {
        // Coming from the trigger: land on the selected option, else the first/last one.
        const fallback = e.key === 'ArrowDown' ? optionEls[0] : optionEls[optionEls.length - 1];
        target = optionEls.find((el) => el.classList.contains('is-selected')) || fallback;
      } else {
        const step = e.key === 'ArrowDown' ? 1 : -1;
        target = optionEls[Math.min(Math.max(currentIndex + step, 0), optionEls.length - 1)];
      }
    } else if (this.isOpen && e.key === 'Home') {
      target = optionEls[0];
    } else if (this.isOpen && e.key === 'End') {
      target = optionEls[optionEls.length - 1];
    } else {
      return;
    }

    e.preventDefault();
    target?.focus();
  }

  /**
   * Option buttons that can currently be picked (sold-out sizes are disabled).
   * @returns {HTMLButtonElement[]}
   */
  getEnabledOptionEls() {
    return Array.from(this.list.querySelectorAll('.cart-bar-mobile__select-option:not(:disabled)'));
  }

  /**
   * Picks the clicked option and re-dispatches it for <cart-bar-mobile> to proxy.
   * @param {MouseEvent} e
   */
  onListClick(e) {
    const optionEl = e.target.closest('.cart-bar-mobile__select-option');
    if (!optionEl || optionEl.disabled) return;

    const value = optionEl.getAttribute('data-option-value');
    const option = this.options.find((o) => o.value === value);
    if (!option) return;

    this.close();
    this.dispatchEvent(new CustomEvent('cart-bar-mobile-select:choose', { detail: option, bubbles: true }));
  }

  /**
   * @param {CartBarMobileOption[]} options
   * @param {string | null} selectedValue
   */
  setOptions(options, selectedValue) {
    this.options = options || [];
    this.selectedValue = selectedValue || null;
    this.renderList();
    this.renderTrigger();
  }

  renderList() {
    const isColor = this.selectType === 'color';
    let html = '';

    for (const opt of this.options) {
      const isSelected = opt.value === this.selectedValue;
      let classes = 'cart-bar-mobile__select-option';
      if (opt.soldOut) classes += ' is-sold-out';
      if (isSelected) classes += ' is-selected';

      const dot = isColor ? colorDotHtml(opt.swatchStyle) : '';
      const soldOutLabel = opt.soldOut ? `<span class="cart-bar-mobile__sold-out">${escapeHtml(this.soldOutText)}</span>` : '';
      const disabledAttr = opt.soldOut && !isColor ? ' disabled' : '';

      html +=
        `<button type="button" class="${classes}" aria-pressed="${isSelected}" ` +
        `data-option-value="${escapeHtml(opt.value)}"${disabledAttr}>` +
        `${dot}<span class="cart-bar-mobile__select-option-text">${escapeHtml(opt.value)}</span>${soldOutLabel}</button>`;
    }

    this.list.innerHTML = html;
  }

  renderTrigger() {
    const selected = this.options.find((o) => o.value === this.selectedValue);

    if (!selected) {
      this.triggerContent.innerHTML = `<span class="cart-bar-mobile__select-option-text">${escapeHtml(this.dataset.placeholder || '')}</span>`;
      return;
    }

    this.clearError();
    const dot = this.selectType === 'color' ? colorDotHtml(selected.swatchStyle) : '';
    this.triggerContent.innerHTML = `${dot}<span class="cart-bar-mobile__select-option-text">${escapeHtml(selected.value)}</span>`;
  }

  showError() {
    this.classList.add('has-error');
  }

  clearError() {
    this.classList.remove('has-error');
  }
}

if (!customElements.get('cart-bar-mobile-select')) {
  customElements.define('cart-bar-mobile-select', CartBarMobileSelect);
}

class CartBarMobile extends HTMLElement {
  constructor() {
    super();
    // Bound once; disconnectedCallback removes every listener, so a reconnect sets up cleanly.
    this._onSelectOpening = this.onSelectOpening.bind(this);
    this._onColorChoose = this.onColorChoose.bind(this);
    this._onVariantChoose = this.onVariantChoose.bind(this);
    this._onButtonClick = this.onButtonClick.bind(this);
    this._onCartChange = this.onCartChange.bind(this);
    this._onCartError = this.onCartError.bind(this);
    this._onMutation = this.onMutation.bind(this);
    this._onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    this.productInfo = this.closest('product-info') || document.querySelector(`product-info[data-section="${sectionId}"]`);
    if (!this.productInfo) return;

    this.sectionId = sectionId;
    this.colorSelect = this.querySelector('[data-select-type="color"]');
    this.variantSelect = this.querySelector('[data-select-type="variant"]');
    this.button = this.querySelector('[data-cart-bar-mobile-button]');

    this.addEventListener('cart-bar-mobile-select:opening', this._onSelectOpening);
    this.colorSelect?.addEventListener('cart-bar-mobile-select:choose', this._onColorChoose);
    this.variantSelect?.addEventListener('cart-bar-mobile-select:choose', this._onVariantChoose);
    this.button?.addEventListener('click', this._onButtonClick);
    document.addEventListener('cart:change', this._onCartChange);
    document.addEventListener('cart:error', this._onCartError);

    this.observer = new MutationObserver(this._onMutation);
    this.observer.observe(this.productInfo, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-available'],
    });

    this.observeBuyButtons();
    this.renderSelects();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.reserveScrollSpace(false);
    this.removeEventListener('cart-bar-mobile-select:opening', this._onSelectOpening);
    this.colorSelect?.removeEventListener('cart-bar-mobile-select:choose', this._onColorChoose);
    this.variantSelect?.removeEventListener('cart-bar-mobile-select:choose', this._onVariantChoose);
    this.button?.removeEventListener('click', this._onButtonClick);
    document.removeEventListener('cart:change', this._onCartChange);
    document.removeEventListener('cart:error', this._onCartError);
    clearTimeout(this.loadingTimeout);
  }

  /**
   * Reveal once the buy box has fully scrolled out of view — live parity
   * (assets/sticky-add-to-cart-c.js): a plain IntersectionObserver on
   * `.product_submit_button_wrap`/the real Add to cart button/the form,
   * default threshold (fires at 0% visible). Unlike the desktop bar, live
   * never overrides this one to be always-on — mobile genuinely is
   * scroll-revealed.
   */
  observeBuyButtons() {
    const target = this.productInfo.querySelector('.product-form__buttons');
    if (!target || !('IntersectionObserver' in window)) return;

    this.visibilityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.classList.toggle('is-visible', !entry.isIntersecting);
        this.reserveScrollSpace(!entry.isIntersecting);
      }
    });
    this.visibilityObserver.observe(target);

    // Bar height changes (or drops to 0 from 750px) — keep the reserved space in sync.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this._onResize);
      this.resizeObserver.observe(this);
    }
  }

  onResize() {
    this.reserveScrollSpace(this.classList.contains('is-visible'));
  }

  /**
   * Reserves the bar's height at the bottom of the scrollport so focused controls scroll into view above it.
   * Shared with the desktop bar (component-cart-bar-pdp.js), so it only clears space it reserved itself.
   * @param {boolean} visible - Whether the bar is currently shown
   */
  reserveScrollSpace(visible) {
    const root = document.documentElement;
    const height = visible ? this.offsetHeight : 0;

    if (height) {
      root.style.scrollPaddingBlockEnd = `${height}px`;
      root.dataset.scrollReserveOwner = this.id;
    } else if (root.dataset.scrollReserveOwner === this.id) {
      root.style.scrollPaddingBlockEnd = '';
      delete root.dataset.scrollReserveOwner;
    }
  }

  /**
   * @param {CartBarMobileOption[]} options
   * @returns {string | null} Value of the first checked option.
   */
  firstChecked(options) {
    const match = options.find((o) => o.checked);
    return match ? match.value : null;
  }

  /**
   * <cart-bar-mobile> renders inside <product-info>, so renderSelects()'s own
   * innerHTML writes (to the select list/trigger) land inside the observed
   * subtree too. Without this guard every re-render would re-trigger the
   * observer on itself — an infinite loop that hangs the tab. Only act on
   * mutations that happened outside this element.
   * @param {MutationRecord[]} mutations
   * @returns {void}
   */
  onMutation(mutations) {
    const isExternal = mutations.some((mutation) => !this.contains(mutation.target));
    if (isExternal) this.renderSelects();
  }

  renderSelects() {
    if (this.colorSelect) {
      const colorOptions = readColorOptions(this.productInfo);
      this.colorSelect.setOptions(colorOptions, this.firstChecked(colorOptions));
    }

    if (this.variantSelect) {
      const sizeOptions = readSizeOptions(this.productInfo);
      this.variantSelect.setOptions(sizeOptions, this.firstChecked(sizeOptions));
    }
  }

  /**
   * Only one select is open at a time.
   * @param {CustomEvent} e
   */
  onSelectOpening(e) {
    if (e.target === this.colorSelect) this.variantSelect?.close();
    if (e.target === this.variantSelect) this.colorSelect?.close();
  }

  /**
   * @param {CustomEvent<CartBarMobileOption>} e
   */
  onColorChoose(e) {
    const option = e.detail;
    // Unlike size, an "unavailable" color is still a real, clickable product
    // page (soldOut here just means "not in stock for the currently picked
    // size") — section-product.js's own sibling-click handler never blocks
    // it either, it's purely an informational slash. Same here.
    option.proxy?.click();
    // The AJAX color swap (section-product.js) replaces the buy box after a
    // fetch; the MutationObserver above re-renders both selects once it lands.
  }

  /**
   * @param {CustomEvent<CartBarMobileOption>} e
   */
  onVariantChoose(e) {
    const option = e.detail;
    if (option.soldOut) return;
    option.proxy?.click();
    // Clicking a radio sets `checked` synchronously (a property, not an
    // attribute) — no mutation fires for that alone, so re-render here too.
    this.renderSelects();
  }

  onButtonClick() {
    const realAddToCartBtn = getRealAddToCartBtn(this.sectionId);

    if (!realAddToCartBtn) {
      // No size (or no size+color combo) resolved yet — same as live,
      // surface it on this bar's own select rather than the main PDP.
      if (this.variantSelect) {
        this.variantSelect.showError();
        // Deferred: the select's own "click outside closes it" document
        // listener would otherwise see this same click and close it again
        // in the same event.
        setTimeout(() => this.variantSelect.open(), 0);
      }

      return;
    }

    this.setLoading(true);
    realAddToCartBtn.click();

    // Safety net, live parity (sticky-add-to-cart-c.js's own 8s fallback):
    // if cart:change/cart:error is somehow missed (e.g. a listener race,
    // or cart.js throwing before it dispatches), the button must not stay
    // stuck disabled/blank forever.
    clearTimeout(this.loadingTimeout);
    this.loadingTimeout = setTimeout(() => this.setLoading(false), 8000);
  }

  /**
   * @param {CustomEvent} e cart:change from cart.js; ignored when another product form caused it.
   */
  onCartChange(e) {
    if (e.detail?.source && !this.productInfo.contains(e.detail.source)) return;
    this.setLoading(false);
  }

  /**
   * @param {CustomEvent} e cart:error from cart.js; ignored when another product form caused it.
   */
  onCartError(e) {
    if (e.detail?.source && !this.productInfo.contains(e.detail.source)) return;
    this.setLoading(false);
  }

  /**
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    if (!this.button) return;
    clearTimeout(this.loadingTimeout);

    // The loader's display:none/block is handled entirely by CSS keyed off
    // this one class (.cart-bar-mobile__btn.is-loading .cart-bar-mobile__btn-loader) —
    // live parity, see component-cart-bar-mobile.css.
    this.button.classList.toggle('is-loading', isLoading);
    this.button.disabled = isLoading;
  }
}

if (!customElements.get('cart-bar-mobile')) {
  customElements.define('cart-bar-mobile', CartBarMobile);
}
